import React, { useRef, useState } from 'react';
import { createGoogleMeetSpace } from '@apis/api/chat';
import { useGoogleLogin } from '@react-oauth/google';
import { useGroupChannel } from '@sendbird/uikit-react/GroupChannel/context';
import { useUserInfoQuery } from '@apis/query/user';
import { Button } from '@components/amc/Button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@components/ui/dropdown-menu';
import { ChatMessageType } from '@asset/enum/chatEnum';
import { UserType } from '@asset/enum/UserEnum';
import * as S from '@styles/chat/chatGroupChannelFooter.style';
import { getUsernameFromNickname, uploadChatFiles } from '@asset/function/chat';
import { toast } from 'react-toastify';
import { useQuery } from 'react-query';
import { usersGoogleAuthCallback } from '@apis/api/auth';
import { useChatStore } from '@store/chat';
import _ from 'lodash';
import GroupChannelFooterInput from './GroupChannelFooterInput';
import { IconCirclePlus, IconPhoto, IconVideo, IconX, IconClock } from '@tabler/icons-react';
import { Oval } from 'react-loader-spinner';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const GroupChannelFooter = ({
  channelInputRef,
  openScheduledMessageFormPopup
}) => {
  const {
    data: user
  } = useUserInfoQuery();
  const {
    clickedMessageToReply,
    setClickedMessageToReply
  } = useChatStore();
  const {
    state: {
      currentChannel
    },
    actions: {
      sendUserMessage,
      sendFileMessage,
      sendMultipleFilesMessage,
      scrollToBottom
    }
  } = useGroupChannel();
  const fileInputRef = useRef(null);
  const [isOpenMessageOptionMenu, setIsOpenMessageOptionMenu] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [isSendingImage, setIsSendingImage] = useState(false);
  const handleSendImageMessage = async files => {
    try {
      setIsSendingImage(true);
      const fileUrls = (await uploadChatFiles(files, () => toast.error('일부 사진 전송에 실패했습니다.'))).map(file => file.url);
      if (fileUrls.length === 0) return;
      if (fileUrls.length === 1) {
        await sendFileMessage({
          fileUrl: fileUrls[0]
        });
      } else {
        await sendMultipleFilesMessage({
          fileInfoList: fileUrls.map(fileUrl => ({
            fileUrl: fileUrl
          }))
        });
      }
      scrollToBottom();
    } finally {
      setIsSendingImage(false);
    }
  };
  const handleFileChange = async event => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setImageFiles(prevFiles => [...prevFiles, ...files]);
    }
    setIsOpenMessageOptionMenu(false);
    if (event.target) {
      event.target.value = null;
    }
  };
  const sendGoogleMeetMessage = async googleMeetUri => {
    await sendUserMessage({
      message: googleMeetUri,
      customType: ChatMessageType.GOOGLE_MEET
    });
  };
  const createGoogleMeetSpaceAfterGoogleLogin = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/meetings.space.created',
    onSuccess: async ({
      code,
      scope
    }) => {
      await usersGoogleAuthCallback(code, scope.split(' '));
      const {
        success,
        data
      } = await createGoogleMeetSpace();
      if (success) {
        sendGoogleMeetMessage(data.uri);
      }
      return null;
    }
  });
  const {
    isFetching: isFetchingGoogleMeet,
    refetch: createGoogleMeet
  } = useQuery(['CREATE_GOOGLE_MEET'], async () => {
    const {
      success,
      data
    } = await createGoogleMeetSpace({
      handleError: (status, data) => {
        if (status === 403 && data.code === 'GOOGLE_OAUTH_TOKEN_REQUIRED') {
          createGoogleMeetSpaceAfterGoogleLogin();
          return true;
        }
        return false;
      }
    });
    if (success) {
      await sendGoogleMeetMessage(data.uri);
      setIsOpenMessageOptionMenu(false);
    }
    return null;
  }, {
    enabled: false
  });
  const messageOptionMenuItems = [{
    id: ChatMessageType.FILE,
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-2",
        children: [/*#__PURE__*/_jsx(IconPhoto, {
          size: 16,
          color: isFetchingGoogleMeet ? 'var(--gray-60)' : 'var(--gray-10)'
        }), /*#__PURE__*/_jsx("span", {
          children: "\uC774\uBBF8\uC9C0 \uCCA8\uBD80"
        })]
      }), /*#__PURE__*/_jsx("input", {
        type: "file",
        multiple: true,
        id: "chat-file-message-input",
        accept: ".svg, .jpg, .png, .jpeg",
        className: "hidden",
        ref: fileInputRef,
        onChange: handleFileChange
      })]
    })
  }];
  if (user?.type === UserType.TEACHER) {
    messageOptionMenuItems.push({
      id: ChatMessageType.GOOGLE_MEET,
      content: /*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-2",
        children: [/*#__PURE__*/_jsx(IconVideo, {
          size: 16,
          color: isFetchingGoogleMeet ? 'var(--gray-60)' : 'var(--gray-10)'
        }), /*#__PURE__*/_jsx("span", {
          children: "\uD654\uC0C1 \uD68C\uC758 \uC2DC\uC791"
        }), isFetchingGoogleMeet && /*#__PURE__*/_jsx(Oval, {
          height: 10,
          width: 10,
          color: "var(--blue-50)",
          secondaryColor: "var(--gray-80)",
          visible: true,
          ariaLabel: "oval-loading",
          strokeWidth: 4,
          strokeWidthSecondary: 4
        })]
      })
    });
    messageOptionMenuItems.push({
      id: ChatMessageType.SCHEDULED,
      content: /*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-2",
        children: [/*#__PURE__*/_jsx(IconClock, {
          size: 16,
          color: isFetchingGoogleMeet ? 'var(--gray-60)' : 'var(--gray-10)'
        }), /*#__PURE__*/_jsx("span", {
          children: "\uBA54\uC2DC\uC9C0 \uC608\uC57D"
        })]
      })
    });
  }
  const replyString = (() => {
    if (!clickedMessageToReply || typeof clickedMessageToReply?.sender?.nickname !== 'string') {
      return '';
    }
    const senderNickname = clickedMessageToReply.sender.nickname;
    if (user?.type === UserType.STUDENT) {
      return senderNickname.startsWith('[S]') ? '나에게 답장' : '선생님에게 답장';
    }
    return `${getUsernameFromNickname(senderNickname)}에게 답장`;
  })();
  const typingUsers = (currentChannel?.getTypingUsers() ?? []).map(user => getUsernameFromNickname(user.nickname));
  const typingString = user ? typingUsers.length > 0 ? user.type === UserType.STUDENT ? '선생님 입력 중...' : typingUsers.join(', ') + ' 입력 중...' : '' : '';
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(S.ChannelFooter, {
      children: [typingUsers.length > 0 && /*#__PURE__*/_jsx(S.TypingIndicator, {
        children: typingString
      }), !_.isNil(clickedMessageToReply) && /*#__PURE__*/_jsxs(S.ReplyingIndicator, {
        children: [/*#__PURE__*/_jsxs("div", {
          className: "header",
          children: [/*#__PURE__*/_jsx("div", {
            className: "counterpart",
            children: replyString
          }), /*#__PURE__*/_jsx("button", {
            onClick: () => setClickedMessageToReply(null),
            children: /*#__PURE__*/_jsx(IconX, {
              size: 16,
              color: "var(--gray-50)"
            })
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: "reply-message-content",
          children: clickedMessageToReply.isFileMessage() || clickedMessageToReply.isMultipleFilesMessage() ? '사진' : clickedMessageToReply.message
        })]
      }), /*#__PURE__*/_jsxs(S.MessageInputContainer, {
        replyString: replyString,
        parentMessageType: clickedMessageToReply ? clickedMessageToReply.isFileMessage() || clickedMessageToReply.isMultipleFilesMessage() ? ChatMessageType.FILE : clickedMessageToReply.messageType : null,
        children: [/*#__PURE__*/_jsxs(DropdownMenu, {
          open: isOpenMessageOptionMenu,
          onOpenChange: open => setIsOpenMessageOptionMenu(open),
          children: [/*#__PURE__*/_jsx(DropdownMenuTrigger, {
            children: /*#__PURE__*/_jsx(Button, {
              variant: "Ghost",
              icon: /*#__PURE__*/_jsx(IconCirclePlus, {}),
              size: "Medium",
              onTouchEnd: e => e.stopPropagation()
            })
          }), /*#__PURE__*/_jsx(DropdownMenuContent, {
            align: "start",
            side: "top",
            className: "w-[10rem]",
            children: messageOptionMenuItems.map(item => /*#__PURE__*/_jsx(DropdownMenuItem, {
              className: isFetchingGoogleMeet ? 'cursor-wait' : '',
              disabled: isFetchingGoogleMeet,
              onSelect: async e => {
                e.stopPropagation();
                e.preventDefault();
                if (item.id === ChatMessageType.SCHEDULED) {
                  openScheduledMessageFormPopup(currentChannel);
                  setIsOpenMessageOptionMenu(false);
                } else if (item.id === ChatMessageType.FILE) {
                  if (!isFetchingGoogleMeet) {
                    fileInputRef.current?.click();
                  }
                } else if (item.id === ChatMessageType.GOOGLE_MEET) {
                  await createGoogleMeet();
                }
              },
              children: item.content
            }, item.id))
          })]
        }), /*#__PURE__*/_jsx(GroupChannelFooterInput, {
          channelInputRef: channelInputRef,
          imageFiles: imageFiles,
          sendImageMessage: handleSendImageMessage,
          setImageFiles: setImageFiles,
          isSendingImage: isSendingImage
        })]
      })]
    })
  });
};
export default GroupChannelFooter;